import React from 'react';

const BlogPosts = [
    {
        slug: 'html-email-guidebook',
        title: {
            EN: <React.Fragment>
                Practical guide for building HTML/CSS emails
            </React.Fragment>,
            ES: <React.Fragment>
                Guía práctica para maquetar correos HTML/CSS
            </React.Fragment>,
        },
        banner: '/assets/img/blog/html-email-guidebook.jpeg',
        postedDate: '2026-09-02',
        updatedDate: null,
        content: {
            EN: <React.Fragment>
                <>
                    <p>
                        <strong>Objective:</strong> create robust, responsive, and maintainable HTML emails that display correctly across as many email clients as possible.
                    </p>

                    <blockquote>
                        <strong>Rule #1:</strong> HTML for email <strong>is not traditional web development</strong>. The goal is not to use the most modern CSS technologies, but to generate HTML that can be correctly interpreted by clients with very different rendering engines.
                    </blockquote>

                    <h2>1. Fundamental principles</h2>

                    <p>Before writing a single line of HTML:</p>

                    <h3>1.1 Design for compatibility, not perfection</h3>

                    <p>There is no HTML that looks exactly the same in all clients.</p>

                    <p>Priority clients are typically:</p>

                    <ul>
                        <li>Gmail</li>
                        <li>Outlook</li>
                        <li>Apple Mail</li>
                        <li>Yahoo Mail</li>
                        <li>Outlook.com</li>
                        <li>iOS Mail</li>
                        <li>Android/Gmail</li>
                        <li>Samsung Mail</li>
                    </ul>

                    <p>Priority should be defined based on the clients used by <strong>your audience</strong>, not just global statistics. Tools like Litmus allow analyzing and testing different clients and devices.</p>

                    <h2>2. The golden rule: use tables for layout</h2>

                    <p>In a modern webpage we would use:</p>

                    <pre><code>{`<div class="container">
    <div class="column">
        ...
    </div>
</div>`}</code></pre>

                    <p>In email, a much safer structure is:</p>

                    <pre><code>{`<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr>
        <td align="center">
            <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0">
                <tr>
                    <td>
                        Content
                    </td>
                </tr>
            </table>
        </td>
    </tr>
</table>`}</code></pre>

                    <h3>Why?</h3>

                    <p>Because some clients, especially certain versions of Outlook, have limited or inconsistent support for modern CSS.</p>

                    <p><strong>For email:</strong></p>

                    <blockquote>
                        Table = layout<br />
                        {'<td>'} = spacing/alignment<br />
                        CSS = styling
                    </blockquote>

                    <h2>3. Recommended width</h2>

                    <p>A very common pattern is:</p>

                    <div style={{ border: '2px solid #333', padding: '20px', margin: '10px 0', textAlign: 'center', fontFamily: 'monospace' }}>
                        <div style={{ border: '1px solid #666', padding: '15px', margin: '10px auto', maxWidth: '600px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5' }}>
                                <strong>100% viewport</strong>
                            </div>
                        </div>
                    </div>

                    <p>Use approximately:</p>

                    <pre><code>{`<table width="600">`}</code></pre>

                    <p>and let the content adapt on small screens.</p>

                    <p>A typical strategy:</p>

                    <pre><code>{`<table role="presentation" width="100%" style="max-width:600px;">`}</code></pre>

                    <p>However, <strong>don&apos;t rely exclusively on {'`max-width`'} for Outlook</strong>; it&apos;s preferable to use a table-compatible structure.</p>

                    <h2>4. CSS: use only what you really need</h2>

                    <p>A big difference from web development is that <strong>CSS support varies greatly between clients</strong>.</p>

                    <p>Before using a CSS property, check: <a href="https://www.caniemail.com/">Can I Email — CSS/HTML compatibility database</a></p>

                    <p>Check:</p>

                    <ul>
                        <li>support</li>
                        <li>problematic clients</li>
                        <li>versions</li>
                        <li>alternative behavior</li>
                    </ul>

                    <p><strong>Avoid depending on:</strong></p>

                    <ul>
                        <li>{'`display: grid;`'}</li>
                        <li>{'`position: absolute;`'}</li>
                        <li>{'`position: fixed;`'}</li>
                        <li>flexbox</li>
                        <li>background-blend-mode</li>
                        <li>complex animations</li>
                        <li>JavaScript</li>
                    </ul>

                    <p>It doesn&apos;t mean <strong>everything</strong> is prohibited. It means you must check it against the clients you support.</p>

                    <h2>5. Inline CSS</h2>

                    <p>For maximum compatibility, a large part of the CSS should end up inline:</p>

                    <pre><code>{`<td style="
    padding:24px;
    font-family:Arial,sans-serif;
    font-size:16px;
    line-height:24px;
    color:#333333;
">`}</code></pre>

                    <p>Instead of depending exclusively on:</p>

                    <pre><code>{`<style>
    .content {
        padding: 24px;
        ...
    }
</style>`}</code></pre>

                    <p>Modern frameworks and tools can make this transformation automatically.</p>

                    <h2>6. Basic reset</h2>

                    <p>An email should normally start with a small reset.</p>

                    <p>Example:</p>

                    <pre><code>{`<style>
    body { margin: 0; padding: 0; }
    table { border-spacing: 0; border-collapse: collapse; }
    img { border: 0; display: block; max-width: 100%; }
    a { text-decoration: none; }
</style>`}</code></pre>

                    <p>But remember:</p>

                    <blockquote>The reset doesn&apos;t replace testing.</blockquote>

                    <h2>7. Use {'`role="presentation"`'}</h2>

                    <p>Tables used only for layout should not be interpreted as data tables by assistive technologies.</p>

                    <p>Use:</p>

                    <pre><code>{`<table role="presentation">`}</code></pre>

                    <p>in your layout tables. This improves accessibility.</p>

                    <h2>8. Images</h2>

                    <p>Images are one of the main sources of problems.</p>

                    <p>Always:</p>

                    <pre><code>{`<img
    src="https://example.com/banner.jpg"
    width="600"
    alt="Banner description"
    style="display:block;border:0;"
>`}</code></pre>

                    <h3>Rules</h3>

                    <p><strong>Always define:</strong></p>

                    <pre><code>{`width="600"`}</code></pre>

                    <p>and:</p>

                    <pre><code>{`alt="..."`}</code></pre>

                    <p>Also:</p>

                    <pre><code>{`display:block;`}</code></pre>

                    <p>This helps avoid unexpected spaces around images.</p>

                    <h3>8.1 Never depend completely on images</h3>

                    <p>Incorrect:</p>

                    <pre><code>{`[ IMAGE WITH ALL CONTENT ]`}</code></pre>

                    <p>If the client blocks images, the user could receive practically an empty email.</p>

                    <p>Better:</p>

                    <div style={{ border: '2px solid #333', padding: '0', margin: '10px 0', fontFamily: 'monospace', maxWidth: '300px' }}>
                        <div style={{ padding: '10px', backgroundColor: '#e0e0e0', borderBottom: '1px solid #333', textAlign: 'center' }}>
                            <strong>IMAGE</strong>
                        </div>
                        <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                            Real heading in HTML
                        </div>
                        <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                            Real text in HTML
                        </div>
                        <div style={{ padding: '10px', backgroundColor: '#f0f0f0', textAlign: 'center' }}>
                            [ CTA ]
                        </div>
                    </div>

                    <h2>9. Buttons: don&apos;t trust solely {'<button>'}</h2>

                    <p>For email, usually use a styled link:</p>

                    <pre><code>{`<a href="https://example.com" style="
    display:inline-block;
    padding:14px 24px;
    background:#000000;
    color:#ffffff;
    font-family:Arial,sans-serif;
    font-size:16px;
    font-weight:bold;
    text-decoration:none;
">
    Buy now
</a>`}</code></pre>

                    <p>But for maximum compatibility with Outlook, especially when you need very precise buttons, consider specific techniques like <strong>VML</strong>.</p>

                    <h2>10. Outlook deserves special treatment</h2>

                    <p>One of the biggest sources of problems is Outlook.</p>

                    <p>Historically some versions of Outlook use the Microsoft Word engine to render HTML.</p>

                    <p>That means:</p>

                    <pre><code>{`Browser ≠ Gmail ≠ Outlook`}</code></pre>

                    <p>That&apos;s why you can have:</p>

                    <pre><code>{`padding: 20px;`}</code></pre>

                    <p>and get different results.</p>

                    <h3>Practical rule</h3>

                    <p>If something works in:</p>

                    <ul>
                        <li>Chrome</li>
                        <li>Firefox</li>
                        <li>Safari</li>
                    </ul>

                    <p><strong>it doesn&apos;t mean it works in Outlook.</strong></p>

                    <p>Always test Outlook.</p>

                    <h2>11. Responsive Design</h2>

                    <p>Never design only for desktop.</p>

                    <p>A typical structure:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <strong>Desktop</strong>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                                <div style={{ flex: 1, border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Product</div>
                                <div style={{ flex: 1, border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Product</div>
                            </div>
                        </div>
                        <div>
                            <strong>Mobile</strong>
                            <div style={{ marginTop: '5px' }}>
                                <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center', marginBottom: '5px' }}>Product</div>
                                <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Product</div>
                            </div>
                        </div>
                    </div>

                    <p>A very used technique is media queries:</p>

                    <pre><code>{`<style>
@media screen and (max-width:600px) {
    .container { width:100% !important; }
    .column { display:block !important; width:100% !important; }
    .mobile-padding { padding:16px !important; }
}
</style>`}</code></pre>

                    <p>But again:</p>

                    <blockquote>Never assume all clients will support exactly the same media queries.</blockquote>

                    <h2>12. Mobile-first when possible</h2>

                    <p>Design first thinking of:</p>

                    <pre><code>{`320px - 430px`}</code></pre>

                    <p>and subsequently expand to desktop.</p>

                    <p>Especially important for:</p>

                    <ul>
                        <li>buttons</li>
                        <li>text</li>
                        <li>images</li>
                        <li>columns</li>
                        <li>spacing</li>
                    </ul>

                    <p>A CTA should be easy to tap with a finger.</p>

                    <h2>13. Typography</h2>

                    <p>Use widely available fonts:</p>

                    <pre><code>{`font-family: Arial, Helvetica, sans-serif;`}</code></pre>

                    <p>or:</p>

                    <pre><code>{`font-family: Georgia, serif;`}</code></pre>

                    <p>Don&apos;t depend on a custom font for the design to work.</p>

                    <p>You can use web fonts in certain clients, but you must provide fallback:</p>

                    <pre><code>{`font-family: 'My Custom Font', Arial, sans-serif;`}</code></pre>

                    <p>The email should still be readable even if the custom font doesn&apos;t load.</p>

                    <h2>14. Explicit {'`line-height`'}</h2>

                    <p>Don&apos;t depend only on the default value.</p>

                    <p>Use:</p>

                    <pre><code>{`font-size:16px;
line-height:24px;`}</code></pre>

                    <p>This produces much more predictable results. Especially in headings, paragraphs, buttons, and lists.</p>

                    <h2>15. Don&apos;t use {'`margin`'} as the main layout mechanism</h2>

                    <p>Margins have inconsistent behavior in some clients.</p>

                    <p>In email it&apos;s usually safer:</p>

                    <pre><code>{`<td style="padding:24px;">`}</code></pre>

                    <p>than:</p>

                    <pre><code>{`margin:24px;`}</code></pre>

                    <h2>16. Use {'`padding`'} instead of spacing hacks</h2>

                    <p>Example:</p>

                    <pre><code>{`<td style="padding:32px 24px;">`}</code></pre>

                    <p>Instead of:</p>

                    <pre><code>{`<div style="margin-top:32px;">`}</code></pre>

                    <p>This makes the layout more predictable.</p>

                    <h2>17. Spacers</h2>

                    <p>A small, very used trick:</p>

                    <pre><code>{`<tr>
    <td height="24" style="font-size:0;line-height:0;">
        &nbsp;
    </td>
</tr>`}</code></pre>

                    <p>It can be useful when you need highly predictable spacing. But avoid abusing them. If you can solve it with {'`padding`'}, it&apos;s usually better.</p>

                    <h2>18. Dark Mode</h2>

                    <p>Currently it&apos;s not enough to only test:</p>

                    <pre><code>{`Light Mode`}</code></pre>

                    <p>Also test:</p>

                    <pre><code>{`Dark Mode`}</code></pre>

                    <p>Typical problems:</p>

                    <pre><code>{`Black logo → invisible
Black text → invisible
Image with transparent background → bad contrast
CTA → modified colors`}</code></pre>

                    <p>When necessary you can use:</p>

                    <pre><code>{`@media (prefers-color-scheme: dark) { ... }`}</code></pre>

                    <p>but <strong>first check the specific client support</strong>.</p>

                    <h2>19. Preheader</h2>

                    <p>The preheader is the text that many clients show next to the subject.</p>

                    <p>Example:</p>

                    <pre><code>{`<div style="display:none;max-height:0;overflow:hidden;opacity:0;">
    Your order is ready to be shipped.
</div>`}</code></pre>

                    <p>It should be:</p>

                    <ul>
                        <li>useful</li>
                        <li>short</li>
                        <li>related to the subject</li>
                    </ul>

                    <p>Avoid showing garbage like:</p>

                    <pre><code>{`View this email in browser...`}</code></pre>

                    <p>as the first visible text in the inbox.</p>

                    <h2>20. Links</h2>

                    <p>Always use complete URLs:</p>

                    <pre><code>{`<a href="https://example.com/account">`}</code></pre>

                    <p>Not:</p>

                    <pre><code>{`<a href="/account">`}</code></pre>

                    <p>The email opens outside the context of your application.</p>

                    <p>Also: validate all links, check tracking, check parameters, check redirects.</p>

                    <h2>21. Semantic HTML and accessibility</h2>

                    <p>Although the layout uses tables, the content should still be accessible.</p>

                    <p>Use:</p>

                    <pre><code>{`<h1>Your order</h1>
<p>Thank you for shopping...</p>`}</code></pre>

                    <p>when the client supports it correctly, but prioritize a structure that survives the least capable clients.</p>

                    <p>For images:</p>

                    <pre><code>{`alt="Company logo"`}</code></pre>

                    <p>For decorative images:</p>

                    <pre><code>{`alt=""`}</code></pre>

                    <h2>22. Don&apos;t put critical information only in CSS</h2>

                    <p>Incorrect:</p>

                    <pre><code>{`.hero {
    background-image:url(...);
}`}</code></pre>

                    <p>if important content depends on that background.</p>

                    <p>Better:</p>

                    <pre><code>{`<img src="hero.jpg" alt="Special offer">`}</code></pre>

                    <p>and place critical content as HTML.</p>

                    <h2>23. Background images</h2>

                    <p>Backgrounds are a complicated area.</p>

                    <p>You may need specific techniques for Outlook.</p>

                    <p>If you use:</p>

                    <pre><code>{`background-image:url(...);`}</code></pre>

                    <p>check the behavior in:</p>

                    <ul>
                        <li>Outlook</li>
                        <li>Gmail</li>
                        <li>Apple Mail</li>
                        <li>mobile</li>
                    </ul>

                    <p>For campaigns where the background is critical, consider using VML/fallbacks or letting MJML handle compatibility.</p>

                    <h2>24. GIFs</h2>

                    <p>GIFs can be useful, but:</p>

                    <ul>
                        <li>increase weight</li>
                        <li>some clients don&apos;t show complete animation</li>
                        <li>the first frame must work as a static image</li>
                    </ul>

                    <p>Therefore:</p>

                    <blockquote>The first frame must contain a valid experience.</blockquote>

                    <p>Never make essential content appear only after several frames.</p>

                    <h2>25. Avoid JavaScript</h2>

                    <p>In general:</p>

                    <pre><code>{`❌`}</code></pre>

                    <p>Email clients are not a reliable JavaScript platform.</p>

                    <p>Don&apos;t build email logic depending on:</p>

                    <ul>
                        <li>{'`fetch()`'}</li>
                        <li>{'`addEventListener()`'}</li>
                        <li>DOM manipulation</li>
                    </ul>

                    <p>The email should work as a static document.</p>

                    <h2>26. Don&apos;t use forms as essential part</h2>

                    <p>Elements like:</p>

                    <pre><code>{`<form>
<input>
<select>
<button>`}</code></pre>

                    <p>have inconsistent support.</p>

                    <p>If you need interaction:</p>

                    <pre><code>{`Email
   ↓
CTA
   ↓
Web/App`}</code></pre>

                    <p>is much more reliable.</p>

                    <h2>27. HTML size</h2>

                    <p>Keep the HTML small.</p>

                    <p>This is especially important for large emails.</p>

                    <p>Avoid:</p>

                    <ul>
                        <li>unnecessary CSS</li>
                        <li>duplicated HTML</li>
                        <li>huge images</li>
                        <li>unnecessary SVG</li>
                        <li>excessive tracking</li>
                        <li>giant components</li>
                    </ul>

                    <p>An email should be as small as possible without sacrificing compatibility.</p>

                    <h2>28. SVG</h2>

                    <p>Don&apos;t assume that:</p>

                    <pre><code>{`<img src="logo.svg">`}</code></pre>

                    <p>will work correctly in all clients.</p>

                    <p>For critical logos:</p>

                    <pre><code>{`PNG/JPG`}</code></pre>

                    <p>is usually a safer option.</p>

                    <p>If you use SVG, verify the target clients.</p>

                    <h2>29. Recommended structure</h2>

                    <p>A maintainable email can be conceptually divided like this:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>Email</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── Preheader</div>
                            <div>├── Header</div>
                            <div>├── Hero</div>
                            <div>├── Content</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── Section</div>
                                <div>├── Section</div>
                                <div>└── Section</div>
                            </div>
                            <div>├── CTA</div>
                            <div>├── Footer</div>
                            <div>└── Unsubscribe</div>
                        </div>
                    </div>

                    <p>This facilitates creating reusable components.</p>

                    <h2>30. Componentization</h2>

                    <p>Instead of creating each email from scratch:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>templates/</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── base</div>
                            <div>├── header</div>
                            <div>├── footer</div>
                            <div>├── button</div>
                            <div>├── hero</div>
                            <div>├── product-card</div>
                            <div>├── two-columns</div>
                            <div>├── social-links</div>
                            <div>└── unsubscribe</div>
                        </div>
                    </div>

                    <p>This way you can have a design system for emails.</p>

                    <h2>31. Recommended framework: MJML</h2>

                    <p>For development teams, my first recommendation is to evaluate <strong>MJML</strong>.</p>

                    <p><a href="https://mjml.io/">MJML</a></p>

                    <p>MJML allows writing:</p>

                    <pre><code>{`<mj-section>
    <mj-column>
        <mj-text>
            Hello World
        </mj-text>
    </mj-column>
</mj-section>`}</code></pre>

                    <p>and converting it to email-compatible HTML.</p>

                    <p>The compiler generates structures with nested tables, inline CSS, and specific compatibility techniques.</p>

                    <h3>Advantages</h3>

                    <ul>
                        <li>responsive</li>
                        <li>components</li>
                        <li>less manual HTML</li>
                        <li>fewer hacks</li>
                        <li>CLI</li>
                        <li>Node.js integration</li>
                        <li>good Outlook compatibility</li>
                        <li>easy to version in Git</li>
                    </ul>

                    <p>For example:</p>

                    <pre><code>{`npm install mjml`}</code></pre>

                    <p>and:</p>

                    <pre><code>{`mjml email.mjml -o email.html`}</code></pre>

                    <h2>32. Alternative framework: React Email</h2>

                    <p>If the team works heavily with React/TypeScript, another interesting option is:</p>

                    <p><a href="https://react.email/">React Email</a></p>

                    <p>Conceptual example:</p>

                    <pre><code>{`<Html>
    <Body>
        <Container>
            <Heading>Welcome!</Heading>
            <Button href="https://example.com">Get started</Button>
        </Container>
    </Body>
</Html>`}</code></pre>

                    <h3>MJML or React Email?</h3>

                    <p>For a team mainly:</p>

                    <pre><code>{`Node.js + HTML/CSS
      ↓
    MJML`}</code></pre>

                    <p>seems like a very natural option.</p>

                    <p>For:</p>

                    <pre><code>{`React + TypeScript
      ↓
React Email`}</code></pre>

                    <p>it may be more comfortable.</p>

                    <h2>33. Bootstrap for emails?</h2>

                    <p><strong>I don&apos;t recommend using Bootstrap directly.</strong></p>

                    <p>Bootstrap is designed for browsers, not for the constraints of email clients.</p>

                    <p>For emails it&apos;s preferable:</p>

                    <pre><code>{`MJML
   ↓
Compatible HTML`}</code></pre>

                    <p>or:</p>

                    <pre><code>{`HTML tables
   ↓
Inline CSS`}</code></pre>

                    <h2>34. Recommended pipeline</h2>

                    <p>For a professional project:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>MJML /<br/>React Email</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compile<br/>HTML</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Inline CSS<br/>/ Optimize</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Automated<br/>tests</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Rendering<br/>QA</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block' }}>
                            <strong>ESP<br/>SendGrid/SES/etc.</strong>
                        </div>
                    </div>

                    <h2>35. Testing: never stay with Chrome</h2>

                    <p>This is probably the most important point of the entire guide.</p>

                    <p>Send the email to:</p>

                    <pre><code>{`Gmail`}</code></pre>

                    <p>and see it well <strong>doesn&apos;t mean it&apos;s finished</strong>.</p>

                    <p>Test at minimum:</p>

                    <h3>Desktop</h3>

                    <ul>
                        <li>Outlook</li>
                        <li>Gmail</li>
                        <li>Apple Mail</li>
                    </ul>

                    <h3>Mobile</h3>

                    <ul>
                        <li>Gmail iOS/Android</li>
                        <li>Apple Mail iOS</li>
                    </ul>

                    <h3>Also</h3>

                    <ul>
                        <li>blocked images</li>
                        <li>dark mode</li>
                        <li>links</li>
                        <li>long text</li>
                        <li>long names</li>
                        <li>dynamic content</li>
                        <li>different screen sizes</li>
                    </ul>

                    <h2>36. Testing tools</h2>

                    <h3>Litmus</h3>

                    <p><a href="https://www.litmus.com/">Litmus Email Testing</a></p>

                    <p>Highly recommended for professional teams. Allows checking different clients and devices and performing QA checks.</p>

                    <h3>Email on Acid</h3>

                    <p><a href="https://www.emailonacid.com/">Email on Acid</a></p>

                    <p>Another alternative for automated rendering and testing. Also offers API to run email tests.</p>

                    <h3>Can I Email</h3>

                    <p><a href="https://www.caniemail.com/">Can I Email</a></p>

                    <p>My recommendation for quickly answering:</p>

                    <blockquote>&quot;Can I use this CSS property?&quot;</blockquote>

                    <h2>37. Automated testing</h2>

                    <p>For a development team, I recommend incorporating into CI/CD at least:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>npm test</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── HTML validation</div>
                            <div>├── broken links</div>
                            <div>├── missing alt</div>
                            <div>├── missing href</div>
                            <div>├── invalid placeholders</div>
                            <div>├── HTML size</div>
                            <div>└── template compilation</div>
                        </div>
                    </div>

                    <p>And subsequently rendering tests through a specialized platform.</p>

                    <h2>38. Variables and dynamic content</h2>

                    <p>Never mix too much business logic with HTML.</p>

                    <p>For example:</p>

                    <pre><code>{`{{customer.name}}`}</code></pre>

                    <p>or the templating system used by your ESP.</p>

                    <p>But you must also test:</p>

                    <pre><code>{`Luis
Christopher
María José
Jean-Pierre
非常に長い名前`}</code></pre>

                    <p>Real data can break designs that seemed perfect.</p>

                    <h2>39. Safe placeholder</h2>

                    <p>Never let a placeholder break HTML.</p>

                    <p>Bad:</p>

                    <pre><code>{`style="color:{{user.color}}"`}</code></pre>

                    <p>if the value is not controlled.</p>

                    <p>Better:</p>

                    <pre><code>{`Fixed HTML/CSS
+
Controlled dynamic data`}</code></pre>

                    <p>The template should be responsible for the layout, not execute arbitrary logic.</p>

                    <h2>40. Security</h2>

                    <p>Never directly insert HTML content coming from the user.</p>

                    <p>Avoid:</p>

                    <pre><code>{`html += userInput;`}</code></pre>

                    <p>if {'`userInput`'} may contain HTML.</p>

                    <p>Dynamic data must be properly escaped.</p>

                    <h2>41. Versioning</h2>

                    <p>Keep templates in Git.</p>

                    <p>Example:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>emails/</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── components/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── button.mjml</div>
                                <div>├── header.mjml</div>
                                <div>└── footer.mjml</div>
                            </div>
                            <div>├── transactional/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── welcome.mjml</div>
                                <div>├── password-reset.mjml</div>
                                <div>└── order-confirmation.mjml</div>
                            </div>
                            <div>├── marketing/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── newsletter.mjml</div>
                                <div>└── promotion.mjml</div>
                            </div>
                            <div>└── package.json</div>
                        </div>
                    </div>

                    <p>This allows: code review, branches, rollback, CI/CD, reuse, change history.</p>

                    <h2>42. Important rule: progressive enhancement</h2>

                    <p>Build first a version that works practically everywhere:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>BASE EXPERIENCE</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compatible</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Responsive</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Dark mode</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block' }}>
                            <strong>Enhancements</strong>
                        </div>
                    </div>

                    <p>Don&apos;t make the email depend on a modern feature.</p>

                    <p>Example:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Outlook</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Basic version</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Gmail</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Improved version</div>
                        </div>
                        <div>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Apple Mail</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Even more enriched version</div>
                        </div>
                    </div>

                    <p>The user should always receive a functional experience.</p>

                    <h2>43. Pre-send checklist</h2>

                    <h3>HTML</h3>

                    <ul>
                        <li>valid HTML</li>
                        <li>tables with {'`role="presentation"`'}</li>
                        <li>widths defined</li>
                        <li>images with {'`alt`'}</li>
                        <li>images with dimensions</li>
                        <li>inline CSS where necessary</li>
                        <li>doesn&apos;t depend on JavaScript</li>
                        <li>doesn&apos;t depend on modern CSS without fallback</li>
                    </ul>

                    <h3>Responsive</h3>

                    <ul>
                        <li>320px</li>
                        <li>375px</li>
                        <li>390px</li>
                        <li>430px</li>
                        <li>desktop</li>
                        <li>responsive columns</li>
                        <li>easy-to-tap buttons</li>
                        <li>readable text</li>
                    </ul>

                    <h3>Outlook</h3>

                    <ul>
                        <li>layout</li>
                        <li>buttons</li>
                        <li>padding</li>
                        <li>backgrounds</li>
                        <li>images</li>
                        <li>fonts</li>
                    </ul>

                    <h3>Gmail</h3>

                    <ul>
                        <li>desktop</li>
                        <li>mobile</li>
                        <li>clipping</li>
                        <li>images</li>
                        <li>links</li>
                    </ul>

                    <h3>Apple Mail</h3>

                    <ul>
                        <li>iPhone</li>
                        <li>macOS</li>
                        <li>dark mode</li>
                    </ul>

                    <h3>Content</h3>

                    <ul>
                        <li>subject</li>
                        <li>preheader</li>
                        <li>links</li>
                        <li>CTA</li>
                        <li>dynamic texts</li>
                        <li>unsubscribe</li>
                        <li>footer</li>
                        <li>contact data</li>
                    </ul>

                    <h3>QA</h3>

                    <ul>
                        <li>images enabled</li>
                        <li>images disabled</li>
                        <li>dark mode</li>
                        <li>long content</li>
                        <li>long names</li>
                        <li>tested links</li>
                        <li>rendering tests</li>
                        <li>plain-text version</li>
                    </ul>

                    <h2>44. The 15 rules the team should memorize</h2>

                    <p>If I had to convert this entire guide into an internal standard, it would be:</p>

                    <ol>
                        <li><strong>Use tables for layout.</strong></li>
                        <li><strong>Don&apos;t assume web HTML = email HTML.</strong></li>
                        <li><strong>Always test Outlook.</strong></li>
                        <li><strong>Keep the container around 600px.</strong></li>
                        <li><strong>Use {'`padding`'} in {'<td>'} before {'`margin`'}.</strong></li>
                        <li><strong>Define {'`font-size`'} and {'`line-height`'}.</strong></li>
                        <li><strong>All images must have {'`alt`'}.</strong></li>
                        <li><strong>Don&apos;t depend on images for critical information.</strong></li>
                        <li><strong>Don&apos;t depend on JavaScript.</strong></li>
                        <li><strong>Use fallbacks for fonts.</strong></li>
                        <li><strong>Design responsive from the beginning.</strong></li>
                        <li><strong>Check CSS in Can I Email before using it.</strong></li>
                        <li><strong>Test dark mode.</strong></li>
                        <li><strong>Test the email on your audience&apos;s real clients.</strong></li>
                        <li><strong>Automate QA and don&apos;t trust only the browser preview.</strong></li>
                    </ol>

                    <h2>45. Recommended stack for a Node.js team</h2>

                    <p>For a team with a modern stack, my recommendation would be:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Git</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>MJML / React Email</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Node.js</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compile / Build</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compatible HTML</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Automated validation</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Litmus / Email on Acid</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '10px' }}>
                            <strong>ESP</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '5px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                <strong>Gmail</strong>
                            </div>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                <strong>Outlook</strong>
                            </div>
                        </div>
                        <div style={{ fontSize: '20px', marginTop: '5px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginTop: '5px' }}>
                            <strong>Apple Mail</strong>
                        </div>
                    </div>

                    <p><strong>For your Node.js + HTML/CSS stack, I would start with MJML</strong> unless the team is already very oriented to React/TypeScript, in which case I would evaluate React Email. MJML is specifically designed to abstract much of the complexities of responsive email and generate the final compatible HTML.</p>

                    <h2>Recommended references</h2>

                    <ul>
                        <li><a href="https://mjml.io/resources">MJML Documentation</a> — framework and responsive components.</li>
                        <li><a href="https://www.caniemail.com/">Can I Email</a> — HTML/CSS compatibility by client.</li>
                        <li><a href="https://react.email/">React Email</a> — React/TypeScript-based alternative.</li>
                        <li><a href="https://www.litmus.com/">Litmus</a> — rendering, QA, and testing.</li>
                        <li><a href="https://www.emailonacid.com/">Email on Acid</a> — client testing and automation.</li>
                    </ul>

                    <h2>Conclusion</h2>

                    <p>The best strategy is not to try to learn all the hacks for Outlook, Gmail, and Apple Mail individually.</p>

                    <p>It is:</p>

                    <p><strong>specialized framework → reusable components → compatible HTML → automated QA → rendering tests → sending.</strong></p>

                    <p>The goal also shouldn&apos;t be that each client is <strong>pixel-perfect</strong>.</p>

                    <p>The goal should be:</p>

                    <blockquote><strong>Functional everywhere, beautiful where supported, and never broken.</strong></blockquote>
                </>
            </React.Fragment>,
            ES: <React.Fragment>
                <>
                    <p>
                        <strong>Objetivo:</strong> crear emails HTML robustos, responsive y mantenibles que se visualicen correctamente en la mayor cantidad posible de clientes de correo.
                    </p>

                    <blockquote>
                        <strong>Regla #1:</strong> HTML para email <strong>no es desarrollo web tradicional</strong>. El objetivo no es utilizar las tecnologías CSS más modernas, sino generar HTML que pueda ser interpretado correctamente por clientes con motores de renderizado muy diferentes.
                    </blockquote>

                    <h2>1. Principios fundamentales</h2>

                    <p>Antes de escribir una sola línea de HTML:</p>

                    <h3>1.1 Diseña para compatibilidad, no para perfección</h3>

                    <p>No existe un HTML que se vea exactamente igual en todos los clientes.</p>

                    <p>Los clientes prioritarios normalmente son:</p>

                    <ul>
                        <li>Gmail</li>
                        <li>Outlook</li>
                        <li>Apple Mail</li>
                        <li>Yahoo Mail</li>
                        <li>Outlook.com</li>
                        <li>iOS Mail</li>
                        <li>Android/Gmail</li>
                        <li>Samsung Mail</li>
                    </ul>

                    <p>La prioridad debe definirse según los clientes utilizados por <strong>tu audiencia</strong>, no únicamente por estadísticas globales. Herramientas como Litmus permiten analizar y probar diferentes clientes y dispositivos.</p>

                    <h2>2. La regla de oro: usa tablas para layout</h2>

                    <p>En una página web moderna utilizaríamos:</p>

                    <pre><code>{`<div class="container">
    <div class="column">
        ...
    </div>
</div>`}</code></pre>

                    <p>En email, una estructura mucho más segura es:</p>

                    <pre><code>{`<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr>
        <td align="center">
            <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0">
                <tr>
                    <td>
                        Contenido
                    </td>
                </tr>
            </table>
        </td>
    </tr>
</table>`}</code></pre>

                    <h3>¿Por qué?</h3>

                    <p>Porque algunos clientes, especialmente determinadas versiones de Outlook, tienen soporte limitado o inconsistente para CSS moderno.</p>

                    <p><strong>Para email:</strong></p>

                    <blockquote>
                        Table = layout<br />
                        {'<td>'} = spacing/alignment<br />
                        CSS = styling
                    </blockquote>

                    <h2>3. Ancho recomendado</h2>

                    <p>Un patrón muy común es:</p>

                    <div style={{ border: '2px solid #333', padding: '20px', margin: '10px 0', textAlign: 'center', fontFamily: 'monospace' }}>
                        <div style={{ border: '1px solid #666', padding: '15px', margin: '10px auto', maxWidth: '600px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5' }}>
                                <strong>100% viewport</strong>
                            </div>
                        </div>
                    </div>

                    <p>Utiliza aproximadamente:</p>

                    <pre><code>{`<table width="600">`}</code></pre>

                    <p>y deja que el contenido se adapte en pantallas pequeñas.</p>

                    <p>Una estrategia típica:</p>

                    <pre><code>{`<table role="presentation" width="100%" style="max-width:600px;">`}</code></pre>

                    <p>Sin embargo, <strong>no dependas exclusivamente de {'`max-width`'} para Outlook</strong>; es preferible utilizar una estructura compatible con tablas.</p>

                    <h2>4. CSS: utiliza únicamente lo que realmente necesitas</h2>

                    <p>Una gran diferencia respecto al desarrollo web es que <strong>el soporte CSS varía mucho entre clientes</strong>.</p>

                    <p>Antes de utilizar una propiedad CSS, consulta: <a href="https://www.caniemail.com/">Can I Email — CSS/HTML compatibility database</a></p>

                    <p>Comprueba:</p>

                    <ul>
                        <li>soporte</li>
                        <li>clientes problemáticos</li>
                        <li>versiones</li>
                        <li>comportamiento alternativo</li>
                    </ul>

                    <p><strong>Evita depender de:</strong></p>

                    <ul>
                        <li>{'`display: grid;`'}</li>
                        <li>{'`position: absolute;`'}</li>
                        <li>{'`position: fixed;`'}</li>
                        <li>flexbox</li>
                        <li>background-blend-mode</li>
                        <li>animaciones complejas</li>
                        <li>JavaScript</li>
                    </ul>

                    <p>No significa que <strong>todo</strong> esté prohibido. Significa que debes comprobarlo contra los clientes que soportas.</p>

                    <h2>5. Inline CSS</h2>

                    <p>Para máxima compatibilidad, una gran parte del CSS debería terminar inline:</p>

                    <pre><code>{`<td style="
    padding:24px;
    font-family:Arial,sans-serif;
    font-size:16px;
    line-height:24px;
    color:#333333;
">`}</code></pre>

                    <p>En lugar de depender exclusivamente de:</p>

                    <pre><code>{`<style>
    .content {
        padding: 24px;
        ...
    }
</style>`}</code></pre>

                    <p>Los frameworks y herramientas modernas pueden hacer esta transformación automáticamente.</p>

                    <h2>6. Reset básico</h2>

                    <p>Un email normalmente debería comenzar con un pequeño reset.</p>

                    <p>Ejemplo:</p>

                    <pre><code>{`<style>
    body { margin: 0; padding: 0; }
    table { border-spacing: 0; border-collapse: collapse; }
    img { border: 0; display: block; max-width: 100%; }
    a { text-decoration: none; }
</style>`}</code></pre>

                    <p>Pero recuerda:</p>

                    <blockquote>El reset no reemplaza el testing.</blockquote>

                    <h2>7. Usa {'`role="presentation"`'}</h2>

                    <p>Las tablas utilizadas únicamente para maquetación no deberían interpretarse como tablas de datos por tecnologías asistivas.</p>

                    <p>Utiliza:</p>

                    <pre><code>{`<table role="presentation">`}</code></pre>

                    <p>en tus tablas de layout. Esto mejora la accesibilidad.</p>

                    <h2>8. Imágenes</h2>

                    <p>Las imágenes son una de las principales fuentes de problemas.</p>

                    <p>Siempre:</p>

                    <pre><code>{`<img
    src="https://example.com/banner.jpg"
    width="600"
    alt="Descripción del banner"
    style="display:block;border:0;"
>`}</code></pre>

                    <h3>Reglas</h3>

                    <p><strong>Siempre define:</strong></p>

                    <pre><code>{`width="600"`}</code></pre>

                    <p>y:</p>

                    <pre><code>{`alt="..."`}</code></pre>

                    <p>También:</p>

                    <pre><code>{`display:block;`}</code></pre>

                    <p>Esto ayuda a evitar espacios inesperados alrededor de las imágenes.</p>

                    <h3>8.1 Nunca dependas completamente de imágenes</h3>

                    <p>Incorrecto:</p>

                    <pre><code>{`[ IMAGEN CON TODO EL CONTENIDO ]`}</code></pre>

                    <p>Si el cliente bloquea imágenes, el usuario podría recibir prácticamente un email vacío.</p>

                    <p>Mejor:</p>

                    <div style={{ border: '2px solid #333', padding: '0', margin: '10px 0', fontFamily: 'monospace', maxWidth: '300px' }}>
                        <div style={{ padding: '10px', backgroundColor: '#e0e0e0', borderBottom: '1px solid #333', textAlign: 'center' }}>
                            <strong>IMAGE</strong>
                        </div>
                        <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                            Heading real en HTML
                        </div>
                        <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
                            Texto real en HTML
                        </div>
                        <div style={{ padding: '10px', backgroundColor: '#f0f0f0', textAlign: 'center' }}>
                            [ CTA ]
                        </div>
                    </div>

                    <h2>9. Botones: no confíes únicamente en {'<button>'}</h2>

                    <p>Para email, utiliza normalmente un enlace estilizado:</p>

                    <pre><code>{`<a href="https://example.com" style="
    display:inline-block;
    padding:14px 24px;
    background:#000000;
    color:#ffffff;
    font-family:Arial,sans-serif;
    font-size:16px;
    font-weight:bold;
    text-decoration:none;
">
    Comprar ahora
</a>`}</code></pre>

                    <p>Pero para máxima compatibilidad con Outlook, especialmente cuando necesitas botones muy precisos, considera técnicas específicas como <strong>VML</strong>.</p>

                    <h2>10. Outlook merece un tratamiento especial</h2>

                    <p>Una de las mayores fuentes de problemas es Outlook.</p>

                    <p>Históricamente algunas versiones de Outlook utilizan el motor de Microsoft Word para renderizar HTML.</p>

                    <p>Eso significa:</p>

                    <pre><code>{`Browser ≠ Gmail ≠ Outlook`}</code></pre>

                    <p>Por eso puedes tener:</p>

                    <pre><code>{`padding: 20px;`}</code></pre>

                    <p>y obtener resultados diferentes.</p>

                    <h3>Regla práctica</h3>

                    <p>Si algo funciona en:</p>

                    <ul>
                        <li>Chrome</li>
                        <li>Firefox</li>
                        <li>Safari</li>
                    </ul>

                    <p><strong>no significa que funcione en Outlook.</strong></p>

                    <p>Siempre prueba Outlook.</p>

                    <h2>11. Responsive Design</h2>

                    <p>Nunca diseñes solamente para desktop.</p>

                    <p>Una estructura típica:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <strong>Desktop</strong>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                                <div style={{ flex: 1, border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Producto</div>
                                <div style={{ flex: 1, border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Producto</div>
                            </div>
                        </div>
                        <div>
                            <strong>Mobile</strong>
                            <div style={{ marginTop: '5px' }}>
                                <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center', marginBottom: '5px' }}>Producto</div>
                                <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Producto</div>
                            </div>
                        </div>
                    </div>

                    <p>Una técnica muy utilizada es media queries:</p>

                    <pre><code>{`<style>
@media screen and (max-width:600px) {
    .container { width:100% !important; }
    .column { display:block !important; width:100% !important; }
    .mobile-padding { padding:16px !important; }
}
</style>`}</code></pre>

                    <p>Pero nuevamente:</p>

                    <blockquote>Nunca asumas que todos los clientes soportarán exactamente las mismas media queries.</blockquote>

                    <h2>12. Mobile-first cuando sea posible</h2>

                    <p>Diseña primero pensando de:</p>

                    <pre><code>{`320px - 430px`}</code></pre>

                    <p>y posteriormente expande hacia desktop.</p>

                    <p>Especialmente importante para:</p>

                    <ul>
                        <li>botones</li>
                        <li>texto</li>
                        <li>imágenes</li>
                        <li>columnas</li>
                        <li>spacing</li>
                    </ul>

                    <p>Un CTA debería ser fácil de tocar con el dedo.</p>

                    <h2>13. Tipografía</h2>

                    <p>Usa fuentes ampliamente disponibles:</p>

                    <pre><code>{`font-family: Arial, Helvetica, sans-serif;`}</code></pre>

                    <p>o:</p>

                    <pre><code>{`font-family: Georgia, serif;`}</code></pre>

                    <p>No dependas de una fuente custom para que el diseño funcione.</p>

                    <p>Puedes utilizar web fonts en determinados clientes, pero debes proporcionar fallback:</p>

                    <pre><code>{`font-family: 'My Custom Font', Arial, sans-serif;`}</code></pre>

                    <p>El email debe seguir siendo legible aunque la fuente custom no cargue.</p>

                    <h2>14. {'`line-height`'} explícito</h2>

                    <p>No dependas únicamente del valor por defecto.</p>

                    <p>Usa:</p>

                    <pre><code>{`font-size:16px;
line-height:24px;`}</code></pre>

                    <p>Esto produce resultados mucho más predecibles. Especialmente en títulos, párrafos, botones y listas.</p>

                    <h2>15. No uses {'`margin`'} como mecanismo principal de layout</h2>

                    <p>Los margins tienen comportamiento inconsistente en algunos clientes.</p>

                    <p>En email normalmente es más seguro:</p>

                    <pre><code>{`<td style="padding:24px;">`}</code></pre>

                    <p>que:</p>

                    <pre><code>{`margin:24px;`}</code></pre>

                    <h2>16. Usa {'`padding`'} en lugar de hacks de spacing</h2>

                    <p>Ejemplo:</p>

                    <pre><code>{`<td style="padding:32px 24px;">`}</code></pre>

                    <p>En lugar de:</p>

                    <pre><code>{`<div style="margin-top:32px;">`}</code></pre>

                    <p>Esto hace que el layout sea más predecible.</p>

                    <h2>17. Espaciadores</h2>

                    <p>Un pequeño truco muy utilizado:</p>

                    <pre><code>{`<tr>
    <td height="24" style="font-size:0;line-height:0;">
        &nbsp;
    </td>
</tr>`}</code></pre>

                    <p>Puede ser útil cuando necesitas spacing altamente predecible. Pero evita abusar de ellos. Si puedes resolverlo con {'`padding`'}, normalmente es mejor.</p>

                    <h2>18. Dark Mode</h2>

                    <p>Actualmente no basta con probar únicamente:</p>

                    <pre><code>{`Light Mode`}</code></pre>

                    <p>También prueba:</p>

                    <pre><code>{`Dark Mode`}</code></pre>

                    <p>Problemas típicos:</p>

                    <pre><code>{`Logo negro → invisible
Texto negro → invisible
Imagen con fondo transparente → mal contraste
CTA → colores modificados`}</code></pre>

                    <p>Cuando sea necesario puedes utilizar:</p>

                    <pre><code>{`@media (prefers-color-scheme: dark) { ... }`}</code></pre>

                    <p>pero <strong>comprueba primero el soporte del cliente específico</strong>.</p>

                    <h2>19. Preheader</h2>

                    <p>El preheader es el texto que muchos clientes muestran junto al subject.</p>

                    <p>Ejemplo:</p>

                    <pre><code>{`<div style="display:none;max-height:0;overflow:hidden;opacity:0;">
    Tu pedido está listo para ser enviado.
</div>`}</code></pre>

                    <p>Debe ser:</p>

                    <ul>
                        <li>útil</li>
                        <li>corto</li>
                        <li>relacionado con el subject</li>
                    </ul>

                    <p>Evita que aparezca basura como:</p>

                    <pre><code>{`View this email in browser...`}</code></pre>

                    <p>como primer texto visible en la bandeja.</p>

                    <h2>20. Links</h2>

                    <p>Siempre utiliza URLs completas:</p>

                    <pre><code>{`<a href="https://example.com/account">`}</code></pre>

                    <p>No:</p>

                    <pre><code>{`<a href="/account">`}</code></pre>

                    <p>El email se abre fuera del contexto de tu aplicación.</p>

                    <p>Además: valida todos los links, verifica tracking, verifica parámetros, verifica redirects.</p>

                    <h2>21. HTML semántico y accesibilidad</h2>

                    <p>Aunque el layout utilice tablas, el contenido debe seguir siendo accesible.</p>

                    <p>Utiliza:</p>

                    <pre><code>{`<h1>Tu pedido</h1>
<p>Gracias por comprar...</p>`}</code></pre>

                    <p>cuando el cliente lo soporte correctamente, pero prioriza una estructura que sobreviva a los clientes menos capaces.</p>

                    <p>Para imágenes:</p>

                    <pre><code>{`alt="Logo de Company"`}</code></pre>

                    <p>Para imágenes decorativas:</p>

                    <pre><code>{`alt=""`}</code></pre>

                    <h2>22. No pongas información crítica únicamente en CSS</h2>

                    <p>Incorrecto:</p>

                    <pre><code>{`.hero {
    background-image:url(...);
}`}</code></pre>

                    <p>si el contenido importante depende de ese background.</p>

                    <p>Mejor:</p>

                    <pre><code>{`<img src="hero.jpg" alt="Oferta especial">`}</code></pre>

                    <p>y coloca el contenido crítico como HTML.</p>

                    <h2>23. Background images</h2>

                    <p>Los backgrounds son una zona complicada.</p>

                    <p>Puedes necesitar técnicas específicas para Outlook.</p>

                    <p>Si utilizas:</p>

                    <pre><code>{`background-image:url(...);`}</code></pre>

                    <p>comprueba el comportamiento en:</p>

                    <ul>
                        <li>Outlook</li>
                        <li>Gmail</li>
                        <li>Apple Mail</li>
                        <li>móvil</li>
                    </ul>

                    <p>Para campañas donde el background es crítico, considera utilizar VML/fallbacks o dejar que MJML gestione la compatibilidad.</p>

                    <h2>24. GIFs</h2>

                    <p>Los GIFs pueden ser útiles, pero:</p>

                    <ul>
                        <li>aumenta el peso</li>
                        <li>algunos clientes no muestran animación completa</li>
                        <li>la primera frame debe funcionar como imagen estática</li>
                    </ul>

                    <p>Por eso:</p>

                    <blockquote>La primera frame debe contener una experiencia válida.</blockquote>

                    <p>Nunca hagas que el contenido esencial aparezca únicamente después de varios frames.</p>

                    <h2>25. Evita JavaScript</h2>

                    <p>En general:</p>

                    <pre><code>{`❌`}</code></pre>

                    <p>Los clientes de correo no son una plataforma JavaScript confiable.</p>

                    <p>No construyas la lógica del email dependiendo de:</p>

                    <ul>
                        <li>{'`fetch()`'}</li>
                        <li>{'`addEventListener()`'}</li>
                        <li>DOM manipulation</li>
                    </ul>

                    <p>El email debe funcionar como documento estático.</p>

                    <h2>26. No uses formularios como parte esencial</h2>

                    <p>Elementos como:</p>

                    <pre><code>{`<form>
<input>
<select>
<button>`}</code></pre>

                    <p>tienen soporte inconsistente.</p>

                    <p>Si necesitas interacción:</p>

                    <pre><code>{`Email
   ↓
CTA
   ↓
Web/App`}</code></pre>

                    <p>Es mucho más confiable.</p>

                    <h2>27. Tamaño del HTML</h2>

                    <p>Mantén el HTML pequeño.</p>

                    <p>Esto es especialmente importante para emails grandes.</p>

                    <p>Evita:</p>

                    <ul>
                        <li>CSS innecesario</li>
                        <li>HTML duplicado</li>
                        <li>imágenes enormes</li>
                        <li>SVG innecesario</li>
                        <li>tracking excesivo</li>
                        <li>componentes gigantes</li>
                    </ul>

                    <p>Un email debería ser lo más pequeño posible sin sacrificar compatibilidad.</p>

                    <h2>28. SVG</h2>

                    <p>No asumas que:</p>

                    <pre><code>{`<img src="logo.svg">`}</code></pre>

                    <p>funcionará correctamente en todos los clientes.</p>

                    <p>Para logos críticos:</p>

                    <pre><code>{`PNG/JPG`}</code></pre>

                    <p>suele ser una opción más segura.</p>

                    <p>Si utilizas SVG, verifica los clientes objetivo.</p>

                    <h2>29. Estructura recomendada</h2>

                    <p>Un email mantenible puede dividirse conceptualmente así:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>Email</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── Preheader</div>
                            <div>├── Header</div>
                            <div>├── Hero</div>
                            <div>├── Content</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── Section</div>
                                <div>├── Section</div>
                                <div>└── Section</div>
                            </div>
                            <div>├── CTA</div>
                            <div>├── Footer</div>
                            <div>└── Unsubscribe</div>
                        </div>
                    </div>

                    <p>Esto facilita crear componentes reutilizables.</p>

                    <h2>30. Componentización</h2>

                    <p>En lugar de crear cada email desde cero:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>templates/</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── base</div>
                            <div>├── header</div>
                            <div>├── footer</div>
                            <div>├── button</div>
                            <div>├── hero</div>
                            <div>├── product-card</div>
                            <div>├── two-columns</div>
                            <div>├── social-links</div>
                            <div>└── unsubscribe</div>
                        </div>
                    </div>

                    <p>Así puedes tener un sistema de diseño para emails.</p>

                    <h2>31. Framework recomendado: MJML</h2>

                    <p>Para equipos de desarrollo, mi primera recomendación es evaluar <strong>MJML</strong>.</p>

                    <p><a href="https://mjml.io/">MJML</a></p>

                    <p>MJML permite escribir:</p>

                    <pre><code>{`<mj-section>
    <mj-column>
        <mj-text>
            Hello World
        </mj-text>
    </mj-column>
</mj-section>`}</code></pre>

                    <p>y convertirlo en HTML compatible para email.</p>

                    <p>El compilador genera estructuras con tablas anidadas, CSS inline y técnicas específicas de compatibilidad.</p>

                    <h3>Ventajas</h3>

                    <ul>
                        <li>responsive</li>
                        <li>componentes</li>
                        <li>menos HTML manual</li>
                        <li>menor cantidad de hacks</li>
                        <li>CLI</li>
                        <li>integración con Node.js</li>
                        <li>buena compatibilidad con Outlook</li>
                        <li>fácil de versionar en Git</li>
                    </ul>

                    <p>Por ejemplo:</p>

                    <pre><code>{`npm install mjml`}</code></pre>

                    <p>y:</p>

                    <pre><code>{`mjml email.mjml -o email.html`}</code></pre>

                    <h2>32. Framework alternativo: React Email</h2>

                    <p>Si el equipo trabaja fuertemente con React/TypeScript, otra opción interesante es:</p>

                    <p><a href="https://react.email/">React Email</a></p>

                    <p>Ejemplo conceptual:</p>

                    <pre><code>{`<Html>
    <Body>
        <Container>
            <Heading>Welcome!</Heading>
            <Button href="https://example.com">Get started</Button>
        </Container>
    </Body>
</Html>`}</code></pre>

                    <h3>¿MJML o React Email?</h3>

                    <p>Para un equipo principalmente:</p>

                    <pre><code>{`Node.js + HTML/CSS
      ↓
    MJML`}</code></pre>

                    <p>me parece una opción muy natural.</p>

                    <p>Para:</p>

                    <pre><code>{`React + TypeScript
      ↓
React Email`}</code></pre>

                    <p>puede resultar más cómodo.</p>

                    <h2>33. ¿Bootstrap para emails?</h2>

                    <p><strong>No recomiendo utilizar Bootstrap directamente.</strong></p>

                    <p>Bootstrap está diseñado para browsers, no para las restricciones de los clientes de email.</p>

                    <p>Para emails es preferible:</p>

                    <pre><code>{`MJML
   ↓
HTML compatible`}</code></pre>

                    <p>o:</p>

                    <pre><code>{`HTML tables
   ↓
Inline CSS`}</code></pre>

                    <h2>34. Pipeline recomendado</h2>

                    <p>Para un proyecto profesional:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>MJML /<br/>React Email</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compile<br/>HTML</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Inline CSS<br/>/ Optimize</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Automated<br/>tests</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Rendering<br/>QA</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block' }}>
                            <strong>ESP<br/>SendGrid/SES/etc.</strong>
                        </div>
                    </div>

                    <h2>35. Testing: nunca te quedes con Chrome</h2>

                    <p>Este es probablemente el punto más importante de toda la guía.</p>

                    <p>Enviar el email a:</p>

                    <pre><code>{`Gmail`}</code></pre>

                    <p>y verlo bien <strong>no significa que esté terminado</strong>.</p>

                    <p>Prueba como mínimo:</p>

                    <h3>Desktop</h3>

                    <ul>
                        <li>Outlook</li>
                        <li>Gmail</li>
                        <li>Apple Mail</li>
                    </ul>

                    <h3>Mobile</h3>

                    <ul>
                        <li>Gmail iOS/Android</li>
                        <li>Apple Mail iOS</li>
                    </ul>

                    <h3>Además</h3>

                    <ul>
                        <li>imágenes bloqueadas</li>
                        <li>dark mode</li>
                        <li>links</li>
                        <li>texto largo</li>
                        <li>nombres largos</li>
                        <li>contenido dinámico</li>
                        <li>diferentes tamaños de pantalla</li>
                    </ul>

                    <h2>36. Herramientas de testing</h2>

                    <h3>Litmus</h3>

                    <p><a href="https://www.litmus.com/">Litmus Email Testing</a></p>

                    <p>Muy recomendable para equipos profesionales. Permite comprobar diferentes clientes y dispositivos y realizar checks de QA.</p>

                    <h3>Email on Acid</h3>

                    <p><a href="https://www.emailonacid.com/">Email on Acid</a></p>

                    <p>Otra alternativa para rendering y testing automatizado. También ofrece API para ejecutar pruebas de email.</p>

                    <h3>Can I Email</h3>

                    <p><a href="https://www.caniemail.com/">Can I Email</a></p>

                    <p>Mi recomendación para responder rápidamente:</p>

                    <blockquote>&quot;¿Puedo utilizar esta propiedad CSS?&quot;</blockquote>

                    <h2>37. Testing automatizado</h2>

                    <p>Para un equipo de desarrollo, recomiendo incorporar al CI/CD al menos:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>npm test</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── HTML validation</div>
                            <div>├── broken links</div>
                            <div>├── missing alt</div>
                            <div>├── missing href</div>
                            <div>├── invalid placeholders</div>
                            <div>├── HTML size</div>
                            <div>└── template compilation</div>
                        </div>
                    </div>

                    <p>Y posteriormente rendering tests mediante una plataforma especializada.</p>

                    <h2>38. Variables y contenido dinámico</h2>

                    <p>Nunca mezcles demasiado la lógica de negocio con el HTML.</p>

                    <p>Por ejemplo:</p>

                    <pre><code>{`{{customer.name}}`}</code></pre>

                    <p>o el sistema de templating que utilice tu ESP.</p>

                    <p>Pero debes probar también:</p>

                    <pre><code>{`Luis
Christopher
María José
Jean-Pierre
非常に長い名前`}</code></pre>

                    <p>Los datos reales pueden romper diseños que parecían perfectos.</p>

                    <h2>39. Placeholder seguro</h2>

                    <p>Nunca permitas que un placeholder rompa HTML.</p>

                    <p>Malo:</p>

                    <pre><code>{`style="color:{{user.color}}"`}</code></pre>

                    <p>si el valor no está controlado.</p>

                    <p>Mejor:</p>

                    <pre><code>{`HTML/CSS fijo
+
datos dinámicos controlados`}</code></pre>

                    <p>La plantilla debería ser responsable del layout, no de ejecutar lógica arbitraria.</p>

                    <h2>40. Seguridad</h2>

                    <p>Nunca insertes directamente contenido HTML proveniente del usuario.</p>

                    <p>Evita:</p>

                    <pre><code>{`html += userInput;`}</code></pre>

                    <p>si {'`userInput`'} puede contener HTML.</p>

                    <p>Los datos dinámicos deben estar correctamente escapados.</p>

                    <h2>41. Versionado</h2>

                    <p>Mantén las plantillas en Git.</p>

                    <p>Ejemplo:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <div><strong>emails/</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            <div>├── components/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── button.mjml</div>
                                <div>├── header.mjml</div>
                                <div>└── footer.mjml</div>
                            </div>
                            <div>├── transactional/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── welcome.mjml</div>
                                <div>├── password-reset.mjml</div>
                                <div>└── order-confirmation.mjml</div>
                            </div>
                            <div>├── marketing/</div>
                            <div style={{ marginLeft: '20px' }}>
                                <div>├── newsletter.mjml</div>
                                <div>└── promotion.mjml</div>
                            </div>
                            <div>└── package.json</div>
                        </div>
                    </div>

                    <p>Esto permite: code review, branches, rollback, CI/CD, reutilización, historial de cambios.</p>

                    <h2>42. Regla importante: progressive enhancement</h2>

                    <p>Construye primero una versión que funcione prácticamente en todos lados:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>BASE EXPERIENCE</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compatible</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Responsive</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Dark mode</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block' }}>
                            <strong>Enhancements</strong>
                        </div>
                    </div>

                    <p>No hagas que el email dependa de una feature moderna.</p>

                    <p>Ejemplo:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Outlook</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Versión básica</div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Gmail</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Versión mejorada</div>
                        </div>
                        <div>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                                <strong>Apple Mail</strong>
                            </div>
                            <div style={{ fontSize: '20px' }}>↓</div>
                            <div>Versión aún más enriquecida</div>
                        </div>
                    </div>

                    <p>El usuario siempre debe recibir una experiencia funcional.</p>

                    <h2>43. Checklist antes de enviar</h2>

                    <h3>HTML</h3>

                    <ul>
                        <li>HTML válido</li>
                        <li>tablas con {'`role="presentation"`'}</li>
                        <li>widths definidos</li>
                        <li>imágenes con {'`alt`'}</li>
                        <li>imágenes con dimensiones</li>
                        <li>CSS inline donde sea necesario</li>
                        <li>no depende de JavaScript</li>
                        <li>no depende de CSS moderno sin fallback</li>
                    </ul>

                    <h3>Responsive</h3>

                    <ul>
                        <li>320px</li>
                        <li>375px</li>
                        <li>390px</li>
                        <li>430px</li>
                        <li>desktop</li>
                        <li>columnas responsive</li>
                        <li>botones fáciles de tocar</li>
                        <li>texto legible</li>
                    </ul>

                    <h3>Outlook</h3>

                    <ul>
                        <li>layout</li>
                        <li>botones</li>
                        <li>padding</li>
                        <li>backgrounds</li>
                        <li>imágenes</li>
                        <li>fuentes</li>
                    </ul>

                    <h3>Gmail</h3>

                    <ul>
                        <li>desktop</li>
                        <li>mobile</li>
                        <li>clipping</li>
                        <li>imágenes</li>
                        <li>links</li>
                    </ul>

                    <h3>Apple Mail</h3>

                    <ul>
                        <li>iPhone</li>
                        <li>macOS</li>
                        <li>dark mode</li>
                    </ul>

                    <h3>Contenido</h3>

                    <ul>
                        <li>subject</li>
                        <li>preheader</li>
                        <li>links</li>
                        <li>CTA</li>
                        <li>textos dinámicos</li>
                        <li>unsubscribe</li>
                        <li>footer</li>
                        <li>datos de contacto</li>
                    </ul>

                    <h3>QA</h3>

                    <ul>
                        <li>imágenes activadas</li>
                        <li>imágenes desactivadas</li>
                        <li>dark mode</li>
                        <li>contenido largo</li>
                        <li>nombres largos</li>
                        <li>links probados</li>
                        <li>rendering tests</li>
                        <li>versión plain-text</li>
                    </ul>

                    <h2>44. Las 15 reglas que el equipo debería memorizar</h2>

                    <p>Si tuviera que convertir toda esta guía en un estándar interno, sería:</p>

                    <ol>
                        <li><strong>Usa tablas para layout.</strong></li>
                        <li><strong>No asumas que HTML web = HTML email.</strong></li>
                        <li><strong>Prueba Outlook siempre.</strong></li>
                        <li><strong>Mantén el container alrededor de 600px.</strong></li>
                        <li><strong>Usa {'`padding`'} en {'<td>'} antes que {'`margin`'}.</strong></li>
                        <li><strong>Define {'`font-size`'} y {'`line-height`'}.</strong></li>
                        <li><strong>Todas las imágenes deben tener {'`alt`'}.</strong></li>
                        <li><strong>No dependas de imágenes para información crítica.</strong></li>
                        <li><strong>No dependas de JavaScript.</strong></li>
                        <li><strong>Usa fallbacks para fuentes.</strong></li>
                        <li><strong>Diseña responsive desde el principio.</strong></li>
                        <li><strong>Comprueba CSS en Can I Email antes de utilizarlo.</strong></li>
                        <li><strong>Prueba dark mode.</strong></li>
                        <li><strong>Testea el email en los clientes reales de tu audiencia.</strong></li>
                        <li><strong>Automatiza el QA y no confíes únicamente en el preview del navegador.</strong></li>
                    </ol>

                    <h2>45. Stack recomendado para un equipo Node.js</h2>

                    <p>Para un equipo con stack moderno, mi recomendación sería:</p>

                    <div style={{ fontFamily: 'monospace', margin: '10px 0', textAlign: 'center' }}>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Git</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>MJML / React Email</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Node.js</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compile / Build</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Compatible HTML</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Automated validation</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '5px' }}>
                            <strong>Litmus / Email on Acid</strong>
                        </div>
                        <div style={{ fontSize: '20px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginBottom: '10px' }}>
                            <strong>ESP</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '5px' }}>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                <strong>Gmail</strong>
                            </div>
                            <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                <strong>Outlook</strong>
                            </div>
                        </div>
                        <div style={{ fontSize: '20px', marginTop: '5px' }}>↓</div>
                        <div style={{ border: '2px solid #333', padding: '10px', backgroundColor: '#f5f5f5', display: 'inline-block', marginTop: '5px' }}>
                            <strong>Apple Mail</strong>
                        </div>
                    </div>

                    <p><strong>Para vuestro stack Node.js + HTML/CSS, empezaría con MJML</strong> salvo que el equipo ya esté muy orientado a React/TypeScript, en cuyo caso evaluaría React Email. MJML está específicamente diseñado para abstraer buena parte de las complejidades de responsive email y generar el HTML final compatible.</p>

                    <h2>Referencias recomendadas</h2>

                    <ul>
                        <li><a href="https://mjml.io/resources">MJML Documentation</a> — framework y componentes responsive.</li>
                        <li><a href="https://www.caniemail.com/">Can I Email</a> — compatibilidad HTML/CSS por cliente.</li>
                        <li><a href="https://react.email/">React Email</a> — alternativa basada en React/TypeScript.</li>
                        <li><a href="https://www.litmus.com/">Litmus</a> — rendering, QA y testing.</li>
                        <li><a href="https://www.emailonacid.com/">Email on Acid</a> — testing de clientes y automatización.</li>
                    </ul>

                    <h2>Conclusión</h2>

                    <p>La mejor estrategia no es intentar aprender todos los hacks de Outlook, Gmail y Apple Mail individualmente.</p>

                    <p>Es:</p>

                    <p><strong>framework especializado → componentes reutilizables → HTML compatible → automated QA → rendering tests → envío.</strong></p>

                    <p>El objetivo tampoco debería ser que cada cliente sea <strong>pixel-perfect</strong>.</p>

                    <p>El objetivo debe ser:</p>

                    <blockquote><strong>Functional everywhere, beautiful where supported, and never broken.</strong></blockquote>
                </>
            </React.Fragment>
        },
    },
    {
        slug: 'ethical-data',
        title: {
            EN: <React.Fragment>
                Ethical usage of data
            </React.Fragment>,
            ES: <React.Fragment>
                Uso ético de los datos
            </React.Fragment>,
        },
        banner: '/assets/img/blog/ethical-data.png',
        postedDate: '2026-07-20',
        updatedDate: null,
        content: {
            EN: <React.Fragment>
                <>
                    <p>
                        We live in an era where data has become one of the most valuable resources
                        for individuals, businesses, and governments. Every time we browse the
                        internet, use a mobile application, make an online purchase, or interact
                        with social media, we generate a significant amount of information. This
                        data helps organizations develop better products, improve services, conduct
                        scientific research, and create innovative solutions powered by artificial
                        intelligence. However, the enormous value of data also comes with great
                        responsibility. The ethical use of data has become essential to ensure that
                        technology benefits society without compromising privacy, security, or
                        individual rights.
                    </p>

                    <h2>Beyond Privacy</h2>

                    <p>
                        When discussing data ethics, many people immediately think about privacy.
                        While protecting personal information is certainly one of its most important
                        aspects, data ethics goes much further. It also involves ensuring that data
                        is representative, that analyses are objective, that findings are presented
                        responsibly, and that decisions based on data are fair and transparent.
                    </p>

                    <p>
                        Throughout the entire data lifecycle—from data collection to analysis and
                        the presentation of results—ethical principles should guide every step of
                        the process. These principles help reduce risks while maximizing the
                        positive impact that data-driven technologies can have on society.
                    </p>

                    <h2>The Impact of Bias in Data Analysis</h2>

                    <p>
                        One of the greatest challenges in data analysis is avoiding bias. Bias is a
                        natural tendency that can influence decisions, often without us realizing
                        it. Several types of bias can affect the quality of data analysis and lead
                        to misleading conclusions.
                    </p>

                    <h3>Confirmation Bias</h3>

                    <p>
                        Confirmation bias occurs when analysts selectively choose data that supports
                        an existing belief or hypothesis. Instead of allowing the evidence to guide
                        the conclusions, they focus only on information that confirms preconceived
                        ideas. This can influence data selection, research methods, and the final
                        interpretation of results.
                    </p>

                    <h3>Selection Bias</h3>

                    <p>
                        Selection bias appears when the data being analyzed does not accurately
                        represent the entire population. If a dataset only reflects a specific
                        group, the conclusions may not apply to the broader audience. For example,
                        analyzing toy sales using only data collected during the holiday season
                        would likely overestimate normal consumer demand throughout the year.
                    </p>

                    <h3>Interpretation Bias</h3>

                    <p>
                        Interpretation bias occurs when analysts misunderstand what the data is
                        actually showing. Numbers alone rarely tell the full story, and context is
                        essential. A metric that appears negative may actually be expected or even
                        desirable depending on the purpose of the system or the environment in which
                        the data was collected.
                    </p>

                    <h3>Information Bias</h3>

                    <p>
                        Information bias can result from incomplete data sources or from outliers
                        that distort the analysis. For instance, if only a few users stream videos
                        twenty-four hours a day, the average viewing time could become
                        significantly inflated, creating a misleading picture of typical user
                        behavior.
                    </p>

                    <h3>Predictive Bias</h3>

                    <p>
                        Predictive bias occurs when historical data is used to make predictions in
                        situations that have changed significantly. A machine learning model trained
                        before a global pandemic or an economic crisis may no longer provide
                        accurate predictions because the environment has evolved. This is especially
                        relevant in artificial intelligence, where many automated decisions rely on
                        historical datasets.
                    </p>

                    <h2>Best Practices for Responsible Data Analysis</h2>

                    <p>
                        Reducing bias requires careful planning and disciplined analytical
                        practices. Analysts should document their assumptions before beginning a
                        project, validate the quality of their data sources, review their
                        methodologies, investigate unusual values, and encourage peer reviews.
                        Having multiple perspectives often helps identify biases that might
                        otherwise go unnoticed.
                    </p>

                    <h2>Privacy and Data Protection</h2>

                    <p>
                        Beyond bias, privacy and security remain some of the most important ethical
                        concerns in data management. People expect organizations to protect their
                        personal information and to use it only for authorized purposes. Whenever
                        personal data is collected, users should clearly understand what
                        information is being gathered, why it is needed, how it will be stored, and
                        who will have access to it.
                    </p>

                    <p>
                        These concerns have become even more significant with the rapid growth of
                        artificial intelligence. AI systems can analyze enormous amounts of data to
                        personalize recommendations, improve healthcare, and automate business
                        processes. However, they also raise important questions about transparency,
                        accountability, and fairness. Many people believe organizations have an
                        ethical obligation to use artificial intelligence responsibly while
                        protecting individual privacy.
                    </p>

                    <h2>Regulations and Accountability</h2>

                    <p>
                        International regulations play a crucial role in promoting responsible data
                        practices. One of the most influential frameworks is the European Union{"'"}s
                        General Data Protection Regulation (GDPR). This regulation establishes clear
                        rules regarding how personal data is collected, processed, stored, and
                        shared, while granting individuals important rights such as accessing,
                        correcting, deleting, or restricting the use of their personal information.
                    </p>

                    <p>
                        Another important concept is Personally Identifiable Information (PII),
                        which includes any information that can directly or indirectly identify an
                        individual, such as names, email addresses, identification numbers, or home
                        addresses. Organizations must implement strong safeguards to prevent
                        unauthorized access or misuse of this information.
                    </p>

                    <h2>The CIA Triad</h2>

                    <p>
                        One of the most widely accepted frameworks for information security is the
                        <strong> CIA Triad</strong>, which stands for{" "}
                        <strong>Confidentiality</strong>, <strong>Integrity</strong>, and{" "}
                        <strong>Availability</strong>.
                    </p>

                    <ul>
                        <li>
                            <strong>Confidentiality:</strong> Ensures that only authorized individuals
                            can access sensitive information through authentication, encryption, and
                            access controls.
                        </li>
                        <li>
                            <strong>Integrity:</strong> Ensures that data remains accurate, consistent,
                            and protected from unauthorized modifications throughout its lifecycle.
                        </li>
                        <li>
                            <strong>Availability:</strong> Ensures that authorized users have reliable
                            access to information whenever it is needed through backups, system
                            maintenance, and disaster recovery planning.
                        </li>
                    </ul>

                    <h2>Personal Reflection</h2>

                    <p>
                        In my opinion, the ethical use of data will remain one of the greatest
                        technological challenges of the coming years. Artificial intelligence, big
                        data analytics, and automation will continue transforming every industry,
                        but their long-term success depends on public trust. That trust can only be
                        earned through transparency, accountability, and respect for individual
                        privacy.
                    </p>

                    <p>
                        In conclusion, ethical data use is about much more than complying with laws
                        and regulations. It requires an ongoing commitment to objectivity, privacy,
                        fairness, and responsible innovation. Data has the power to transform our
                        world for the better, but only when it is collected, analyzed, and used with
                        integrity and respect. As future professionals working with data and
                        technology, we must remember that behind every dataset are real people whose
                        rights and privacy deserve to be protected at all times.
                    </p>
                </>
            </React.Fragment>,
            ES: <React.Fragment>
                <>
                    <p>
                        Vivimos en una era donde los datos se han convertido en uno de los recursos más valiosos
                        para las personas, las empresas y los gobiernos. Cada vez que navegamos por Internet,
                        utilizamos una aplicación móvil, realizamos una compra en línea o interactuamos con una
                        red social, generamos una gran cantidad de información. Estos datos permiten desarrollar
                        mejores productos, optimizar servicios, realizar investigaciones científicas y crear
                        soluciones basadas en inteligencia artificial. Sin embargo, el enorme valor de los datos
                        también trae consigo una gran responsabilidad. El uso ético de los datos se ha convertido
                        en un aspecto fundamental para garantizar que la tecnología beneficie a la sociedad sin
                        poner en riesgo la privacidad, la seguridad ni los derechos de las personas.
                    </p>

                    <h2>Más allá de la privacidad</h2>

                    <p>
                        Cuando se habla de ética en el manejo de los datos, muchas personas piensan únicamente en
                        la privacidad. Aunque proteger la información personal es uno de los pilares más
                        importantes, la ética de los datos va mucho más allá. También implica asegurar que los
                        datos sean representativos, que el análisis se realice de forma objetiva, que los
                        resultados se presenten con responsabilidad y que las decisiones basadas en ellos sean
                        justas y transparentes.
                    </p>

                    <p>
                        Durante todo el ciclo de vida de un proyecto de análisis de datos, desde la recopilación
                        hasta la presentación de resultados, deben aplicarse principios éticos que minimicen los
                        riesgos y maximicen el beneficio para la sociedad.
                    </p>

                    <h2>El impacto de los sesgos en el análisis</h2>

                    <p>
                        Uno de los mayores desafíos es evitar los diferentes tipos de sesgos que pueden afectar un
                        análisis. El sesgo es una tendencia natural que puede influir en nuestras decisiones,
                        incluso sin que seamos conscientes de ello. En el análisis de datos existen varios tipos
                        de sesgos que pueden producir resultados incorrectos o poco representativos.
                    </p>

                    <h3>Sesgo de confirmación</h3>

                    <p>
                        Ocurre cuando el analista selecciona únicamente los datos que respaldan una hipótesis
                        previa. En lugar de permitir que los datos conduzcan a una conclusión objetiva, se buscan
                        únicamente las evidencias que confirman una idea preconcebida. Esto puede afectar la
                        selección de las fuentes de información, la metodología utilizada e incluso la
                        interpretación de los resultados.
                    </p>

                    <h3>Sesgo de selección</h3>

                    <p>
                        Aparece cuando los datos utilizados no representan adecuadamente a toda la población. Si
                        una muestra es demasiado pequeña o se obtiene únicamente de un grupo específico, las
                        conclusiones pueden ser incorrectas. Un ejemplo sencillo sería analizar las ventas de
                        juguetes utilizando únicamente información recopilada durante la temporada navideña,
                        cuando la demanda es mucho mayor que el resto del año.
                    </p>

                    <h3>Sesgo de interpretación</h3>

                    <p>
                        Este sesgo ocurre cuando los datos se interpretan de forma equivocada. Muchas veces los
                        números por sí solos no cuentan toda la historia y es necesario comprender el contexto
                        antes de sacar conclusiones. Un dato aparentemente negativo puede tener una explicación
                        completamente diferente cuando se analiza el propósito del sistema o las características
                        del entorno donde fue generado.
                    </p>

                    <h3>Sesgo de información</h3>

                    <p>
                        El sesgo de información aparece cuando las fuentes de datos contienen limitaciones
                        importantes o cuando existen valores atípicos que alteran los resultados. Por ejemplo, si
                        un pequeño número de usuarios mantiene una plataforma de transmisión funcionando las
                        veinticuatro horas del día, el promedio general de tiempo de uso puede aumentar
                        considerablemente, dando una impresión equivocada sobre el comportamiento de la mayoría de
                        los usuarios.
                    </p>

                    <h3>Sesgo predictivo</h3>

                    <p>
                        Surge cuando se utilizan datos históricos para realizar predicciones en condiciones
                        completamente diferentes. Un modelo entrenado con información previa a una crisis
                        económica o una pandemia puede producir resultados inexactos porque las circunstancias han
                        cambiado significativamente. Este tipo de sesgo es especialmente importante en la
                        inteligencia artificial, donde muchas decisiones automatizadas dependen de datos
                        recopilados en el pasado.
                    </p>

                    <h2>Buenas prácticas para un análisis responsable</h2>

                    <p>
                        Para reducir estos problemas, los analistas deben adoptar buenas prácticas durante todo el
                        proceso de análisis. Es recomendable documentar las hipótesis antes de comenzar, validar
                        cuidadosamente las fuentes de información, revisar los métodos utilizados, identificar
                        valores atípicos y permitir que otros miembros del equipo revisen el trabajo realizado. La
                        colaboración ayuda a detectar sesgos que una sola persona podría pasar por alto.
                    </p>

                    <h2>Privacidad y protección de la información</h2>

                    <p>
                        Además del problema de los sesgos, la privacidad y la seguridad de los datos representan
                        una de las principales preocupaciones éticas en la actualidad. Las personas esperan que
                        las organizaciones protejan su información personal y la utilicen únicamente con fines
                        autorizados. Cuando una empresa recopila datos personales, debe hacerlo con el
                        consentimiento informado del usuario, explicando claramente qué información será
                        utilizada, cómo será almacenada y con qué propósito.
                    </p>

                    <p>
                        Este aspecto cobra aún más importancia con el crecimiento de la inteligencia artificial.
                        Los sistemas inteligentes pueden analizar enormes cantidades de información para
                        personalizar recomendaciones, mejorar diagnósticos médicos o automatizar procesos
                        empresariales. Sin embargo, estas mismas capacidades también generan preocupación sobre
                        cómo se utilizan los datos personales y hasta qué punto las decisiones automatizadas son
                        transparentes y justas.
                    </p>

                    <h2>Regulaciones y responsabilidad</h2>

                    <p>
                        Las regulaciones internacionales desempeñan un papel importante en el uso responsable de
                        los datos. Uno de los marcos legales más conocidos es el Reglamento General de Protección
                        de Datos (GDPR) de la Unión Europea. Esta normativa establece reglas claras sobre el
                        tratamiento de los datos personales, otorgando a los ciudadanos derechos como acceder a su
                        información, corregirla, eliminarla, limitar su procesamiento y oponerse a determinados
                        usos.
                    </p>

                    <p>
                        Otro concepto fundamental es la protección de la Información de Identificación Personal
                        (PII), que incluye cualquier dato que permita identificar directa o indirectamente a una
                        persona, como nombres, direcciones, correos electrónicos o números de identificación. Las
                        organizaciones deben implementar medidas adecuadas para evitar que esta información sea
                        divulgada o utilizada sin autorización.
                    </p>

                    <h2>La tríada CIA</h2>

                    <p>
                        En materia de seguridad de la información, uno de los modelos más importantes es la tríada
                        CIA, compuesta por <strong>Confidencialidad</strong>,{" "}
                        <strong>Integridad</strong> y <strong>Disponibilidad</strong>.
                    </p>

                    <ul>
                        <li>
                            <strong>Confidencialidad:</strong> garantiza que únicamente las personas autorizadas
                            puedan acceder a la información mediante mecanismos como autenticación, cifrado y
                            control de permisos.
                        </li>
                        <li>
                            <strong>Integridad:</strong> asegura que los datos permanezcan correctos y no sean
                            alterados durante su almacenamiento o transmisión.
                        </li>
                        <li>
                            <strong>Disponibilidad:</strong> busca que la información esté accesible para los
                            usuarios autorizados cuando la necesiten mediante respaldos, mantenimiento de sistemas y
                            planes de recuperación ante desastres.
                        </li>
                    </ul>

                    <h2>Reflexión personal</h2>

                    <p>
                        En mi opinión, el uso ético de los datos será uno de los mayores desafíos tecnológicos de
                        los próximos años. La inteligencia artificial, el análisis de grandes volúmenes de
                        información y la automatización seguirán creciendo, pero su éxito dependerá de la
                        confianza que las personas depositen en quienes administran sus datos. Esa confianza solo
                        puede construirse mediante transparencia, responsabilidad y respeto por la privacidad.
                    </p>

                    <p>
                        En conclusión, el uso ético de los datos no consiste únicamente en cumplir con la
                        legislación vigente. Implica asumir un compromiso permanente con la objetividad, la
                        protección de la información personal, la eliminación de sesgos y el desarrollo de
                        tecnologías responsables. Los datos tienen el potencial de transformar positivamente
                        nuestra sociedad, pero solo cuando son utilizados con integridad, respeto y
                        responsabilidad. Como futuros profesionales relacionados con el análisis de datos y la
                        tecnología, debemos comprender que detrás de cada dato existe una persona cuyos derechos y
                        privacidad deben ser protegidos en todo momento.
                    </p>
                </>
            </React.Fragment>
        },
        podcast: {
            EN: "https://open.spotify.com/embed/episode/1r9T3sdBJJNHzge3BOOyXo?utm_source=generator&si=dfaa54f69cab4395",
            ES: "https://open.spotify.com/embed/episode/5zlUhiTZEG9uAS1BkH5h3i?utm_source=generator&si=2241c2e2aca54192"
        }
    }
]

export default BlogPosts;